import { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import Icon from '@components/Icon';
import { Modal, Button, Tooltip, Divider, Space } from 'antd';
import stores from '@stores';
import { cn, getMargin } from '@utils/utils';
import sizeConfig from '@utils/sizeConfig';
import CustomSize from './CustomSize';


export default observer(() => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isShowSize = stores.editor.img?.src || stores.option.size.type !== 'auto';
    
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };
    
    const handleCancel = () => {
        setIsModalOpen(false);
    };
    
    const checkSelected = (key, item) => {
        if (key !== stores.option.size.type) return false;
        if (item.height !== stores.option.frameConf.height) return false;
        if (item.width !== stores.option.frameConf.width) return false;
        return true;
    }
    
    const onSet = (value) => {
        setIsModalOpen(false);
        if (value.type === 'auto' && stores.editor.img.width) {
            const margin = getMargin(stores.editor.img.width, stores.editor.img.height);
            stores.option.setSize({...value, width: stores.editor.img.width + margin, height: stores.editor.img.height + margin});
            return;
        }
        stores.option.setSize(value);
    };
    
    const toSelected = (key, title, item) => {
        setIsModalOpen(false);
        stores.option.setSize({
            type: key,
            title: `${ title }${ item.title ? ` ${item.title} `:' ' }${ item.w } : ${ item.h }`,
            width: item.width,
            height: item.height
        });
    }
    
    return (
        <div className="mb-3">
            <div className="[&_label]:font-semibold [&_label]:text-sm mb-2">
                <label className="flex items-center">
                    <Icon.LayoutGrid size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                    画布设置
                </label>
            </div>
            
            <div 
                className={cn('px-3 py-1.5 border shrink-0 border-gray-200 dark:border-gray-700 gap-3 shadow-sm overflow-hidden max-h-12 rounded-md hover:border-purple-500 [&_svg]:hover:text-purple-500 cursor-pointer flex items-center')} 
                onClick={handleOpenModal}
            >
                <div
                    className='border border-black/50 bg-black/10 dark:bg-white/20 dark:border-white/40 w-4 rounded-sm'
                    style={{ aspectRatio: stores.option.frameConf.width / stores.option.frameConf.height }}
                />
                <div className='text-xs'>
                    <div className='font-semibold leading-3 mb-0.5'>{stores.option.size.title}</div>
                    {!isShowSize ? 
                        <div className='text-gray-500 leading-3'>自适应截图尺寸</div> :
                        <div className='text-gray-500 leading-3'>
                            {stores.option.frameConf.width} x {stores.option.frameConf.height} px
                        </div>
                    }
                </div>
                <div className='flex-1'></div>
                <Icon.ChevronDown size={16} />
            </div>
            
            <Modal
                title={
                    <div className="flex items-center text-purple-600 dark:text-purple-400">
                        <Icon.LayoutGrid size={20} className="mr-2" />
                        <span>画布设置</span>
                    </div>
                }
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose={true}
                width={720}
                className="canvas-size-modal"
                maskStyle={{ backdropFilter: "blur(4px)" }}
                bodyStyle={{ padding: "12px", background: stores.editor.isDark ? "#1f2937" : "#f8fafc" }}
                style={{ top: 20 }}
            >
                <div className="mb-3 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                    <Divider orientation="left" orientationMargin="0" className="text-xs text-gray-500 dark:text-gray-400 my-1">
                        <div className="flex items-center">
                            <Icon.Edit3 size={12} className="mr-1 text-purple-500 dark:text-purple-400" />
                            自定义尺寸
                        </div>
                    </Divider>
                    <CustomSize type={stores.option.size.type} frameWidth={stores.option.frameConf.width} frameHeight={stores.option.frameConf.height} onSet={onSet} />
                </div>
                
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                    {sizeConfig.map(item => (
                        <div key={item.key} className="mb-3 last:mb-0">
                            <Divider orientation="left" orientationMargin="0" className="text-xs text-gray-500 dark:text-gray-400 my-1">
                                <div className="flex items-center">
                                    <Icon.Maximize size={12} className="mr-1 text-purple-500 dark:text-purple-400" />
                                    {item.title}
                                </div>
                            </Divider>
                            <Space wrap size={[8, 10]} className="w-full">
                                {item.lists.map((child, index) => (
                                    <Button
                                        key={index}
                                        type={checkSelected(item.key, child) ? "primary" : "default"}
                                        className={`p-2 h-auto flex flex-col gap-0 ${
                                            checkSelected(item.key, child)
                                                ? 'bg-purple-600 hover:bg-purple-700 border-purple-600 hover:border-purple-700 text-white'
                                                : 'border-gray-300 dark:border-gray-600 hover:text-purple-600 hover:border-purple-600 dark:hover:text-purple-400 dark:hover:border-purple-400'
                                        }`}
                                        onClick={() => toSelected(item.key, item.title, child)}
                                    >
                                        <div className="py-1 px-2 w-full">
                                            <div
                                                className={`border ${checkSelected(item.key, child) ? 'border-white/70 bg-white/20' : 'border-black/50 bg-black/10 dark:bg-white/20 dark:border-white/40'} w-full flex items-center justify-center rounded-md opacity-75`}
                                                style={{ aspectRatio: child.w / child.h, minWidth: '60px', minHeight: '36px' }}
                                            >
                                                <span className="text-xs">{child.w} : {child.h}</span>
                                            </div>
                                        </div>
                                        {child.title && <div className="text-xs">{child.title}</div>}
                                        <div className={`text-xs overflow-hidden ${checkSelected(item.key, child) ? 'text-white/80' : 'text-gray-500'}`}>
                                            {child.width} x {child.height}
                                        </div>
                                    </Button>
                                ))}
                            </Space>
                        </div>
                    ))}
                </div>
                
                <div className="mt-3 flex justify-end">
                    <Button onClick={handleCancel} className="mr-2 hover:text-purple-600 hover:border-purple-600">
                        取消
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleCancel}
                        className="bg-purple-600 hover:bg-purple-700 border-purple-600 hover:border-purple-700"
                    >
                        确定
                    </Button>
                </div>
            </Modal>
        </div>
    );
});
